import { useEffect, useRef, useState } from 'react';
import type {
  AudioSegment,
  Exposure,
  PlaybackContext,
} from '../components/NarrationPlayer.tsx';

type Source =
  | { kind: 'lesson'; segment: AudioSegment }
  | {
      kind: 'reply';
      url: string;
      text: string;
      durationMs?: number;
      words: AudioSegment['words'];
      timingMethod?: string;
      voice?: string;
      playbackRate?: number;
    };

export type ReplySpeechMetadata = {
  durationMs?: number;
  words?: AudioSegment['words'];
  timingMethod?: string;
  voice?: string;
};

export type ReplyVoiceOptions = {
  /** A calmer per-reply pace; retained independently from the user setting. */
  playbackRate?: number;
};

/** One audio element owns the tutor's voice. Interruptions snapshot the lesson
 * synchronously, before a question can enter the serialized server queue. */
export function useTutorVoice({
  onExposure,
  onLessonEnd,
  onReplyEnd,
}: {
  onExposure: (event: Exposure) => void;
  onLessonEnd: (id: string) => void;
  onReplyEnd?: (url:string) => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const sourceRef = useRef<Source | undefined>(undefined);
  const savedLesson = useRef<
    { segment: AudioSegment; positionMs: number } | undefined
  >(undefined);
  const rangeStart = useRef<number | null>(null);
  const callbacks = useRef({ onExposure, onLessonEnd, onReplyEnd });
  callbacks.current = { onExposure, onLessonEnd, onReplyEnd };
  const [source, setSource] = useState<Source>();
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState('');
  const [rate, setRate] = useState(0.9);
  const generation = useRef(0);
  const animationFrame = useRef<number | undefined>(undefined);
  const lastClockUpdate = useRef(0);

  function sourceDuration(source: Source | undefined, audio = audioRef.current) {
    if (source?.kind === 'lesson') return source.segment.durationMs;
    if (source?.kind === 'reply' && source.durationMs !== undefined)
      return source.durationMs;
    return audio && Number.isFinite(audio.duration)
      ? Math.round(audio.duration * 1000)
      : 0;
  }
  function syncClock(exact = false) {
    const current = audioRef.current;
    if (!current) return;
    const now = Math.round(current.currentTime * 1000);
    if (!exact && now - lastClockUpdate.current < 33) return;
    lastClockUpdate.current = now;
    setPosition(now);
    setDuration(sourceDuration(sourceRef.current, current));
    snapshot();
  }
  function stopClock(exact = false) {
    if (animationFrame.current !== undefined) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = undefined;
    }
    if (exact) syncClock(true);
  }
  function startClock() {
    stopClock();
    const tick = () => {
      syncClock();
      animationFrame.current = requestAnimationFrame(tick);
    };
    animationFrame.current = requestAnimationFrame(tick);
  }

  function snapshot(): PlaybackContext | undefined {
    const s = sourceRef.current;
    if (s?.kind === 'lesson' && audioRef.current) {
      const positionMs = Math.min(
        s.segment.durationMs,
        Math.round(audioRef.current.currentTime * 1000),
      );
      savedLesson.current = { segment: s.segment, positionMs };
      return {
        segmentId: s.segment.id,
        positionMs,
        playing: !audioRef.current.paused,
      };
    }
    const saved = savedLesson.current;
    return saved
      ? {
          segmentId: saved.segment.id,
          positionMs: saved.positionMs,
          playing: false,
        }
      : undefined;
  }
  function flush(event: Exposure['event']) {
    const s = sourceRef.current;
    const a = audioRef.current;
    const from = rangeStart.current;
    rangeStart.current = null;
    if (s?.kind !== 'lesson' || !a) return;
    const to = Math.min(s.segment.durationMs, Math.round(a.currentTime * 1000));
    snapshot();
    if (from !== null && to > from)
      callbacks.current.onExposure({
        audioId: s.segment.id,
        event,
        positionMs: to,
        fromMs: from,
        toMs: to,
        durationMs: s.segment.durationMs,
      });
    else if (event === 'skipped' || event === 'replayed')
      callbacks.current.onExposure({
        audioId: s.segment.id,
        event,
        positionMs: to,
      });
  }
  function pause() {
    generation.current++;
    audioRef.current?.pause();
    stopClock(true);
    flush('stopped');
    setPlaying(false);
    return snapshot();
  }
  async function play() {
    if (!audioRef.current || !sourceRef.current) return;
    setError('');
    const requested = ++generation.current;
    try {
      await audioRef.current.play();
    } catch {
      if (requested === generation.current)
        setError('Tap Play to hear the tutor, or open the transcript to read.');
    }
  }
  function load(next: Source, autoplay: boolean, at = 0) {
    pause();
    sourceRef.current = next;
    setSource(next);
    setPosition(at);
    setDuration(
      next.kind === 'lesson' ? next.segment.durationMs : next.durationMs ?? 0,
    );
    setError('');
    const a = audioRef.current;
    if (!a) return;
    a.src = next.kind === 'lesson' ? next.segment.url : next.url;
    a.playbackRate = next.kind === 'reply' && next.playbackRate !== undefined
      ? next.playbackRate
      : rate;
    a.currentTime = at / 1000;
    if (next.kind === 'lesson')
      savedLesson.current = { segment: next.segment, positionMs: at };
    if (autoplay) void play();
  }
  function lesson(segment: AudioSegment | undefined, autoplay = false, at = 0) {
    if (!segment) {
      clear();
      setError(
        'Voice is unavailable for this part. Open the transcript to read it.',
      );
      return;
    }
    load({ kind: 'lesson', segment }, autoplay, at);
  }
  function seek(at: number) {
    const s = sourceRef.current;
    const a = audioRef.current;
    if (!a || !s) return;
    flush('stopped');
    const max =
      s.kind === 'lesson'
        ? s.segment.durationMs
        : Number.isFinite(a.duration)
          ? a.duration * 1000
          : 0;
    a.currentTime = Math.max(0, Math.min(at, max)) / 1000;
    syncClock(true);
    if (!a.paused) rangeStart.current = Math.round(a.currentTime * 1000);
    snapshot();
  }
  function resumeLesson() {
    const saved = savedLesson.current;
    if (!saved) return;
    // Resume at the preceding authored sentence, so a mid-word interruption
    // never makes the explanation restart in the middle of a thought.
    const at =
      saved.segment.checkpoints
        .filter((c) => c.startMs <= saved.positionMs)
        .at(-1)?.startMs ?? 0;
    lesson(saved.segment, true, at);
  }
  function skip() {
    pause();
    flush('skipped');
  }
  function clear() {
    pause();
    sourceRef.current = undefined;
    savedLesson.current = undefined;
    setSource(undefined);
    setPosition(0);
    setDuration(0);
    setError('');
    audioRef.current?.removeAttribute('src');
  }
  useEffect(
    () => () => {
      stopClock();
      audioRef.current?.pause();
    },
    [],
  );

  const audioProps = {
    ref: audioRef,
    preload: 'metadata',
    onLoadedMetadata: () => {
      syncClock(true);
    },
    onPlay: () => {
      setPlaying(true);
      rangeStart.current = Math.round(
        (audioRef.current?.currentTime ?? 0) * 1000,
      );
      startClock();
    },
    onPause: () => {
      stopClock(true);
      setPlaying(false);
      flush('stopped');
    },
    onTimeUpdate: () => {
      // The rAF clock owns smooth playback updates. This keeps a final exact
      // value for browsers that coalesce animation frames in the background.
      if (audioRef.current?.paused) syncClock(true);
    },
    onEnded: () => {
      stopClock(true);
      const ended = sourceRef.current;
      flush('played');
      setPlaying(false);
      if (ended?.kind === 'lesson')
        callbacks.current.onLessonEnd(ended.segment.id);
      else if(ended?.kind==='reply') callbacks.current.onReplyEnd?.(ended.url);
    },
    onError: () => {
      stopClock(true);
      setPlaying(false);
      flush('stopped');
      setError(
        'This audio could not load. Read the transcript or try Play again.',
      );
    },
  };
  return {
    audioProps,
    playing,
    position,
    duration,
    source,
    error,
    rate,
    setRate: (next: number) => {
      setRate(next);
      if (audioRef.current) audioRef.current.playbackRate = next;
    },
    setPlaybackRate: (next: number) => {
      setRate(next);
      if (audioRef.current) audioRef.current.playbackRate = next;
    },
    clearError: () => setError(''),
    pause,
    play,
    lesson,
    seek,
    skip,
    clear,
    resumeLesson,
    reply: (
      url: string,
      text: string,
      metadata?: ReplySpeechMetadata,
      options?: ReplyVoiceOptions,
    ) =>
      load(
        {
          kind: 'reply',
          url,
          text,
          durationMs: metadata?.durationMs,
          words: metadata?.words ?? [],
          timingMethod: metadata?.timingMethod,
          voice: metadata?.voice,
          playbackRate: options?.playbackRate,
        },
        true,
      ),
    replay: () => {
      flush('replayed');
      seek(0);
      void play();
    },
    snapshot,
  };
}
